import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { formatDate as formatJurisdictionDate, getActiveJurisdiction } from '@/lib/jurisdiction/jurisdiction-config'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: Date | string, locale?: string): string {
  const opts: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }
  return formatJurisdictionDate(
    date,
    locale ? { ...opts, config: { ...getActiveJurisdiction(), locale } } : opts,
  )
}

export function formatDateTime(date: Date | string, locale?: string): string {
  const opts: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }
  return formatJurisdictionDate(
    date,
    locale ? { ...opts, config: { ...getActiveJurisdiction(), locale } } : opts,
  )
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

/**
 * Coerce any value into a safe array.
 *
 * Tolerant of the common shapes our APIs return so a `.map/.filter/.forEach`
 * never throws `x.map is not a function`:
 *  - a bare array            -> returned as-is
 *  - a paginated envelope     -> `items` / `results` / `data` / `dimensions`
 *  - `null` / `undefined`     -> `[]`
 *  - any other (object/scalar)-> `[]`  (the unsafe `(x ?? []).map` case)
 *
 * The crucial difference from `x ?? []` is that a *truthy non-array* (e.g. an
 * object `{}` or a `{ dimensions: {...} }` map) is normalised to `[]` instead of
 * being handed straight to `.map`.
 */
export function asArray<T = unknown>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[]
  if (value && typeof value === 'object') {
    const env = value as Record<string, unknown>
    const candidate = env.items ?? env.results ?? env.data ?? env.dimensions
    if (Array.isArray(candidate)) return candidate as T[]
  }
  return []
}

export function truncate(str: string, length: number): string {
  if (str.length <= length) return str
  return str.slice(0, length) + '...'
}

export function slugify(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

export function generateId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36)
}

// Risk severity colors
export const riskSeverityColors = {
  critical: 'text-risk-critical bg-risk-critical/10',
  high: 'text-risk-high bg-risk-high/10',
  medium: 'text-risk-medium bg-risk-medium/10',
  low: 'text-risk-low bg-risk-low/10',
  negligible: 'text-risk-negligible bg-risk-negligible/10',
}

// Maturity level colors
export const maturityLevelColors = {
  initial: 'text-maturity-initial bg-maturity-initial/10',
  developing: 'text-maturity-developing bg-maturity-developing/10',
  defined: 'text-maturity-defined bg-maturity-defined/10',
  managed: 'text-maturity-managed bg-maturity-managed/10',
  optimizing: 'text-maturity-optimizing bg-maturity-optimizing/10',
}

/**
 * Export an array of objects as CSV and trigger download
 */
export function exportToCSV<T extends Record<string, unknown>>(
  data: T[],
  filename: string,
  columns?: { key: keyof T; label: string }[]
): void {
  if (!data.length) return
  const cols = columns ?? Object.keys(data[0]).map((k) => ({ key: k as keyof T, label: String(k) }))
  const header = cols.map((c) => `"${String(c.label)}"`).join(',')
  const rows = data.map((row) =>
    cols.map((c) => {
      const val = row[c.key]
      if (val == null) return '""'
      const str = String(val).replace(/"/g, '""')
      return `"${str}"`
    }).join(',')
  )
  const csv = [header, ...rows].join('\n')
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * Export data as JSON and trigger download
 */
export function exportToJSON(data: unknown, filename: string): void {
  const json = JSON.stringify(data, null, 2)
  const blob = new Blob([json], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.json`
  a.click()
  URL.revokeObjectURL(url)
}
